import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2, PenLine, Save } from "lucide-react";
import { toast } from "sonner";

const LABELS = {
  portfolio_position: "Portfolio Position",
  immediate_priorities: "Immediate Priorities",
  twelve_month_recommendation: "12-Month Recommendation",
  twenty_four_month_outlook: "24-Month Outlook",
  key_risks: "Key Risks",
  data_confidence: "Data Confidence",
  management_recommendation: "Management Recommendation",
  decision_required: "Decision Required",
};

export function ExecSummary() {
  const { client } = useCapital();
  const { role } = usePermissions();
  const canEdit = ["executive", "admin", "finance_admin", "ops_mgmt"].includes((role || "").toLowerCase());
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDoc(null); setEditing(false);
    if (!client) return;
    api.get("/capital/ai/exec-summary", { params: { client } }).then((r) => setDoc(r.data)).catch(() => {});
  }, [client]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/capital/ai/exec-summary/generate", null, { params: { client }, timeout: 120000 });
      setDoc(r.data);
      toast.success("Executive summary generated — review and edit before publishing");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI unavailable");
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const sections = { ...draft, key_risks: (draft.key_risks || "").split("\n").map((s) => s.trim()).filter(Boolean) };
      await api.put("/capital/ai/exec-summary", { client, sections });
      setDoc({ ...doc, sections });
      setEditing(false);
      toast.success("Executive summary saved — it appears in the Board Report PDF");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const startEdit = () => {
    const s = doc?.sections || {};
    setDraft({ ...s, key_risks: Array.isArray(s.key_risks) ? s.key_risks.join("\n") : (s.key_risks || "") });
    setEditing(true);
  };

  const sections = doc?.sections;
  return (
    <div className="mt-6 rounded-md border border-violet-200 bg-violet-50/30 p-4" data-testid="exec-summary-block">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-violet-600" />
        <div className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold">Structured executive summary</div>
        {doc?.generated_at && <span className="text-[10px] text-slate-400">{doc.generated_at.slice(0, 16).replace("T", " ")}{doc.edited_by ? ` · edited by ${doc.edited_by}` : ""}</span>}
        {canEdit && (
          <div className="ml-auto flex gap-1.5 print:hidden">
            {sections && !editing && (
              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={startEdit} data-testid="exec-summary-edit-btn">
                <PenLine className="w-3 h-3 mr-1" /> Edit
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-6 px-2 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                    onClick={generate} disabled={busy} data-testid="exec-summary-generate-btn">
              {busy && !editing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
              {sections ? "Regenerate with AI" : "Generate with AI"}
            </Button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-3 space-y-3 print:hidden">
          {Object.keys(LABELS).map((k) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">
                {LABELS[k]}{k === "key_risks" && " (one per line)"}
              </div>
              <Textarea rows={k === "key_risks" ? 4 : 2} value={draft[k] || ""}
                        onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                        data-testid={`exec-summary-field-${k}`} className="bg-white text-sm" />
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="sm" className="h-7 bg-violet-600 hover:bg-violet-700 text-white" onClick={save} disabled={busy} data-testid="exec-summary-save-btn">
              <Save className="w-3 h-3 mr-1" /> Save
            </Button>
            <Button size="sm" variant="outline" className="h-7" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : sections ? (
        <div className="mt-3 grid md:grid-cols-2 gap-x-6 gap-y-3">
          {Object.keys(LABELS).map((k) => (
            <div key={k} data-testid={`exec-summary-view-${k}`}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{LABELS[k]}</div>
              {k === "key_risks" && Array.isArray(sections[k]) ? (
                <ul className="text-sm text-slate-700 mt-0.5 list-disc pl-4 space-y-0.5">
                  {sections[k].map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              ) : (
                <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{Array.isArray(sections[k]) ? sections[k].join(" ") : sections[k]}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400 mt-2 italic">
          No structured summary yet{canEdit ? " — click Generate with AI, then review and edit before publishing." : "."}
        </p>
      )}
    </div>
  );
}
